import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { motion, AnimatePresence } from 'motion/react';
import { RiLoader2Line } from '@remixicon/react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { authClient } from '@/lib/auth-client';
import { cn } from '@/lib/utils';

export function VerifyEmailPage() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false });
  const email = (search as { email?: string })?.email;
  const [isResending, setIsResending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isError, setIsError] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const currentTheme = document.documentElement.classList.contains('dark') ? 'dark' : 'light';
  const logoSrc = currentTheme === 'dark' ? '/moniva-light.svg' : '/moniva-dark.svg';

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Auto-verify when all digits are filled
  useEffect(() => {
    const code = otp.join('');
    if (code.length === 6 && !isVerifying) {
      handleVerifyCode(code);
    }
  }, [otp]);

  const handleVerifyCode = async (code: string) => {
    if (!code || code.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }

    if (!email) {
      toast.error('Email address not found');
      return;
    }

    setIsVerifying(true);
    setIsError(false);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/api/verify-email-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, code }),
        credentials: 'include',
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({ message: 'Failed to verify email' }));
        throw new Error(data.message || 'Failed to verify email');
      }

      const data = await response.json();

      toast.success('Email verified successfully! Redirecting to dashboard...');

      // Wait a bit to ensure cookie is set, then reload auth state
      await new Promise(resolve => setTimeout(resolve, 300));

      // Force refresh the session from the backend
      await authClient.$fetch('/api/auth/get-session', {
        method: 'GET',
        credentials: 'include',
      });

      // Wait to ensure session is fully loaded
      await new Promise(resolve => setTimeout(resolve, 500));

      // Navigate to dashboard - the session should now be active
      window.location.href = '/dashboard';
    } catch (error: any) {
      // Trigger error animation
      setIsError(true);
      toast.error(error.message || 'Invalid or expired code');

      setOtp(['', '', '', '', '', '']);
      setTimeout(() => {
        setIsError(false);
        inputRefs.current[0]?.focus();
      }, 500);

      console.error('Verification error:', error);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
setOtp(newOtp);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newOtp = pastedData.split('').concat(Array(6 - pastedData.length).fill(''));
    setOtp(newOtp as string[]);

    const nextIndex = Math.min(pastedData.length, 5);
    inputRefs.current[nextIndex]?.focus();
  };

  const handleResendEmail = async () => {
    if (!email) {
      toast.error('Email address not found');
      return;
    }

    setIsResending(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/api/auth/send-verification-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        throw new Error('Failed to resend verification email');
      }

      toast.success('Verification email sent! Please check your inbox.');
      setCountdown(60);
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } catch (error) {
      toast.error('Failed to resend verification email');
      console.error('Resend email error:', error);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen p-4 bg-gradient-to-br from-background via-background to-muted/20">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className="w-full max-w-md space-y-6"
      >
        <Card className="overflow-hidden shadow-soft-lg border-border/50">
          <CardHeader className="pb-6 space-y-4 text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.1, type: 'spring', stiffness: 100 }}
              className="flex justify-center mx-auto"
            >
              <motion.img
                src={logoSrc}
                alt="Moniva"
                className="w-16 h-16"
                animate={{ rotate: [0, 5, -5, 5, 0] }}
                transition={{ delay: 0.5, duration: 0.5 }}
              />
            </motion.div>

            <CardTitle className="text-2xl font-bold">Check your email</CardTitle>
            <CardDescription>
              We've sent a verification code to
              {email && (
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="block mt-1 font-medium text-foreground"
                >
                  {email}
                </motion.span>
              )}
            </CardDescription>
          </CardHeader>

          <CardContent>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="space-y-6"
            >
              <div className="space-y-2 text-sm text-center text-muted-foreground">
                <p>Enter the 6-digit code from your email</p>
                <p className="text-xs">If you don't see the email, check your spam folder.</p>
              </div>

              <motion.div
                className="flex justify-center gap-2"
                animate={isError ? {
                  x: [0, -5, 5, -5, 5, 0],
                  transition: { duration: 0.4 }
                } : {}}
              >
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={index === 0 ? handlePaste : undefined}
                    disabled={isVerifying}
                    className={cn(
                      "w-12 h-14 text-center text-2xl font-semibold rounded-xl border-2 transition-all duration-300 shadow-sm",
                      "focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary focus:shadow-md",
                      "disabled:opacity-50 disabled:cursor-not-allowed",
                      isError
                        ? "border-red-500 bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400 animate-shake"
                        : digit
                        ? "border-primary bg-primary/5 shadow-md"
                        : "border-input bg-background hover:border-primary/50 hover:shadow-md"
                    )}
                  />
                ))}
              </motion.div>

              <AnimatePresence>
                {isVerifying && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="flex items-center justify-center gap-2 text-sm text-muted-foreground"
                  >
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                      className="w-4 h-4 border-2 rounded-full border-primary border-t-transparent"
                    />
                    Verifying your code...
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="px-2 bg-background text-muted-foreground">
                    or
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full transition-all"
                  onClick={handleResendEmail}
                  disabled={isResending || countdown > 0}
                >
                  {isResending ? (
                    <>
                      <motion.div
                        // animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                      >
                        <RiLoader2Line className="mr-2 size-4" />
                      </motion.div>
                      Sending...
                    </>
                  ) : countdown > 0 ? (
                    `Resend code in ${countdown}s`
                  ) : (
                    <>
                      <RiLoader2Line className="mr-2 size-4" />
                      Resend verification code
                    </>
                  )}
                </Button>

                <Button
                  variant="ghost"
                  className="w-full transition-all"
                  onClick={() => navigate({ to: '/login' })}
                >
                  Back to sign in
                </Button>
              </div>
            </motion.div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
